import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Moon, LayoutGrid, MessageCircleHeart, Activity, HeartHandshake, Users, LogOut, Feather, Crown, Settings, Loader2, Cpu } from "lucide-react";

const NAV = [
  { to: "/home", label: "Home", icon: Moon },
  { to: "/dashboard", label: "Blueprint", icon: LayoutGrid },
  { to: "/companion", label: "Companion", icon: MessageCircleHeart },
  { to: "/journal", label: "Journal", icon: Feather },
  { to: "/compatibility", label: "Compatibility", icon: HeartHandshake },
  { to: "/community", label: "Community", icon: Users },
];

export default function Shell({ children }) {
  const { logout, user } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const tier = user?.tier || "free";
  const [managing, setManaging] = useState(false);

  const doLogout = async () => { await logout(); nav("/"); };

  const doManage = async () => {
    setManaging(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch {
      toast.error("Could not open the billing portal — please try again");
      setManaging(false);
    }
  };

  return (
    <div className="min-h-screen celestial">
      <header className="glass sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/home" data-testid="nav-logo" className="text-2xl font-serif tracking-tight text-primary">
            My<span className="text-foreground">Nsight</span>
          </Link>
          <nav className="hidden md:flex items-center gap-1">
            {NAV.map(({ to, label, icon: Icon }) => {
              const active = loc.pathname === to;
              return (
                <Link key={to} to={to} data-testid={`nav-${label.toLowerCase()}`}
                  className={`flex items-center gap-2 px-3 py-2 rounded-full text-sm transition-colors ${active ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                  <Icon size={15} /> {label}
                </Link>
              );
            })}
          </nav>
          <div className="flex items-center gap-4">
            <Link to="/pricing" data-testid="membership-pill"
              className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-widest border transition-colors ${tier === "free" ? "border-border text-muted-foreground hover:text-primary hover:border-primary/50" : "border-primary/50 text-primary"}`}>
              <Crown size={12} /> {tier}
            </Link>
            {user?.role === "admin" && (
              <Link to="/admin/ai" data-testid="nav-admin-ai" title="AI models"
                className={`flex items-center gap-1.5 text-sm transition-colors ${loc.pathname === "/admin/ai" ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>
                <Cpu size={15} /> <span className="hidden lg:inline">AI models</span>
              </Link>
            )}
            {user?.has_subscription && (
              <button onClick={doManage} disabled={managing} data-testid="header-manage-plan-btn"
                title="Manage plan"
                className="hidden sm:flex items-center gap-1.5 text-muted-foreground hover:text-primary text-sm transition-colors disabled:opacity-50">
                {managing ? <Loader2 size={15} className="animate-spin" /> : <Settings size={15} />}
                <span className="hidden lg:inline">Manage plan</span>
              </button>
            )}
            <button onClick={doLogout} data-testid="logout-btn"
              className="flex items-center gap-2 text-muted-foreground hover:text-foreground text-sm transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Exit</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-10">{children}</main>
      <nav className="md:hidden fixed bottom-0 inset-x-0 glass z-50 flex justify-around py-2">
        {NAV.map(({ to, label, icon: Icon }) => (
          <Link key={to} to={to} className={`flex flex-col items-center text-[10px] gap-0.5 px-2 py-1 ${loc.pathname === to ? "text-primary" : "text-muted-foreground"}`}>
            <Icon size={18} /> {label}
          </Link>
        ))}
      </nav>
    </div>
  );
}
